// src/layouts/openPages/live/Openlayer.js
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { fromLonLat } from 'ol/proj';
import Map from 'ol/Map';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import Feature from 'ol/Feature';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';

import bus from '../../../assets/ship.png';
import stopIcon from '../../../assets/marker_stop.png';
import { defaults as defaultControls, Zoom } from 'ol/control';
import 'ol/ol.css'; // make sure you have this if not already

import { Box, ToggleButton, Tooltip } from '@mui/material';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import haversine from 'haversine';
import { useTheme } from '@mui/material/styles';

function Openlayer({ position, thingModel, userLocation }) {
  const mapEl = useRef(null);
  const [map, setMap] = useState(null);
  const [follow, setFollow] = useState(false);

  const liveSrc = useRef(new VectorSource()).current;
  const coveredSrc = useRef(new VectorSource()).current;
  const remainSrc = useRef(new VectorSource()).current;
  const stopsSrc = useRef(new VectorSource()).current;
  const userSrc = useRef(new VectorSource()).current;

  const view = useRef(new View({ zoom: 5, maxZoom: 20 })).current;
  const popupRef = useRef(null);
  const contentRef = useRef(null);
  const distanceElRef = useRef(null);
  const fromMeElRef = useRef(null);
const theme = useTheme();
  /* ---------- 1. Polyline Decoder ---------- */
  const decodePolyline = (encoded) => {
    if (!encoded?.trim()) return [];
    const poly = [];
    let index = 0, len = encoded.length, lat = 0, lng = 0;
    try {
      while (index < len) {
        let b, shift = 0, result = 0;
        do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
        const dlat = result & 1 ? ~(result >> 1) : result >> 1; lat += dlat;
        shift = 0; result = 0;
        do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
        const dlng = result & 1 ? ~(result >> 1) : result >> 1; lng += dlng;
        poly.push([lng / 1e5, lat / 1e5]);
      }
    } catch (e) {
      console.error('Polyline decode error:', e);
    }
    return poly;
  };

  /* ---------- 2. Draw Line ---------- */
  const drawLine = (coords, color, src) => {
    src.clear();
    if (!coords || coords.length < 2) return;
    const line = new LineString(coords.map(c => fromLonLat(c)));
    const feature = new Feature({ geometry: line });
    feature.setStyle(new Style({ stroke: new Stroke({ color, width: 5, lineCap: 'round' }) }));
    src.addFeature(feature);
  };

  /* ---------- 3. Add Stops ---------- */
  const addStops = useCallback((stops, curLat, curLng) => {
    stopsSrc.clear();
    if (!stops || curLat == null || curLng == null) return;

    const arr = Array.isArray(stops) ? stops : Object.values(stops);
    const valid = arr.filter(s => s?.location?.latitude != null && s?.location?.longitude != null);
    if (!valid.length) return;

    const hasOrder = valid.every(s => s.sequence != null || s.order != null || s.index != null);
    if (hasOrder) {
      valid.sort((a, b) => {
        const seqA = a.sequence ?? a.order ?? a.index ?? 0;
        const seqB = b.sequence ?? b.order ?? b.index ?? 0;
        return seqA - seqB;
      });
    } else {
      valid.sort((a, b) => {
        const distA = haversine(
          { latitude: a.location.latitude, longitude: a.location.longitude },
          { latitude: curLat, longitude: curLng },
          { unit: 'meter' }
        );
        const distB = haversine(
          { latitude: b.location.latitude, longitude: b.location.longitude },
          { latitude: curLat, longitude: curLng },
          { unit: 'meter' }
        );
        return distA - distB;
      });
    }

    valid.forEach((s, i) => {
      const coord = fromLonLat([s.location.longitude, s.location.latitude]);
      const f = new Feature({
        geometry: new Point(coord),
        name: s.locationName || `Stop ${i + 1}`,
        type: 'stop',
        sequence: i + 1
      });
      f.setStyle(new Style({
        image: new Icon({ anchor: [0.5, 1], src: stopIcon, scale: 0.35 }),
        text: new Text({
          text: `${i + 1}`,
          offsetY: -18,
          fill: new Fill({ color: '#fff' }),
stroke: new Stroke({ color: '#000', width: 2 }),          font: 'bold 14px sans-serif'
        })
      }));
      stopsSrc.addFeature(f);
    });
  }, [stopsSrc]);

  /* ---------- 4. MAP INIT ---------- */
  useEffect(() => {
    if (!mapEl.current) return;

    const layers = [
      new TileLayer({ source: new OSM() }),
      new VectorLayer({ source: coveredSrc, zIndex: 10 }),
      new VectorLayer({ source: remainSrc, zIndex: 11 }),
      new VectorLayer({ source: stopsSrc, zIndex: 12 }),
      new VectorLayer({ source: userSrc, zIndex: 13 }),
      new VectorLayer({ source: liveSrc, zIndex: 14 })
    ];

const olMap = new Map({
  target: mapEl.current,
  layers,
  view,
  controls: defaultControls({ 
    attribution: false, // remove OSM ©
    zoom: false          // ✅ disables top-left + / - buttons
  }).extend([
    new Zoom({ className: 'mui-zoom-control' }) // bottom-right MUI zoom
  ])
});

// Apply MUI styles to zoom buttons after map renders
setTimeout(() => {
  const zoomEl = document.querySelector('.mui-zoom-control');
  if (!zoomEl) return;

  // Wrap in a MUI Box using inline sx
  Object.assign(zoomEl.style, {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    boxShadow: '0 3px 6px rgba(0,0,0,0.25)',
    background: theme.palette.background.paper,
    overflow: 'hidden',
    zIndex: 1000
  });

  const buttons = zoomEl.querySelectorAll('button');
  buttons.forEach((btn) => {
    Object.assign(btn.style, {
      width: '40px',
      height: '40px',
      fontSize: '20px',
      border: 'none',
      cursor: 'pointer',
      color: theme.palette.text.primary,
      background: 'transparent',
      transition: 'all 0.25s ease'
    });
    btn.addEventListener('mouseenter', () => {
      btn.style.background = theme.palette.primary.main;
      btn.style.color = theme.palette.primary.contrastText;
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'transparent';
      btn.style.color = theme.palette.text.primary;
    });
  });
}, 300);

    // Popup
    const popEl = document.createElement('div');
    popEl.style.cssText = `background:#fff;border-radius:12px;padding:12px;min-width:180px;box-shadow:0 4px 12px rgba(0,0,0,.15);position:relative;font-family:Roboto;`;
    const closer = document.createElement('button');
    closer.innerHTML = 'x';
    closer.style.cssText = 'position:absolute;top:4px;right:8px;background:none;border:none;font-size:18px;cursor:pointer;';
    closer.onclick = () => popupRef.current?.setPosition(undefined);
    const content = document.createElement('div');
    contentRef.current = content;
    popEl.appendChild(closer);
    popEl.appendChild(content);
    const popup = new Overlay({ element: popEl, autoPan: true, autoPanAnimation: { duration: 250 } });
    olMap.addOverlay(popup);
    popupRef.current = popup;

    // Remaining Distance (top-left)
    const distanceEl = document.createElement('div');
    distanceEl.style.cssText = `
      position:absolute;top:16px;left:16px;
      background:rgba(0,0,0,0.85);color:white;padding:10px 14px;
      border-radius:10px;font-family:Roboto,sans-serif;
      font-size:15px;font-weight:600;z-index:1000;
      pointer-events:none;backdrop-filter:blur(6px);
      box-shadow:0 2px 8px rgba(0,0,0,0.3);min-width:140px;line-height:1.4;
    `;
    olMap.getViewport().appendChild(distanceEl);
    distanceElRef.current = distanceEl;
    olMap.addOverlay(new Overlay({ element: distanceEl, positioning: 'top-left', stopEvent: false }));

    // Distance From Me (below remaining)
    const fromMeEl = document.createElement('div');
    fromMeEl.style.cssText = `
      position:absolute;top:78px;left:16px;
      background:rgba(33,150,243,0.9);color:white;padding:8px 12px;
      border-radius:8px;font-family:Roboto,sans-serif;
      font-size:14px;font-weight:500;z-index:1000;
      pointer-events:none;backdrop-filter:blur(4px);
      box-shadow:0 2px 6px rgba(0,0,0,0.2);min-width:140px;
    `;
    olMap.getViewport().appendChild(fromMeEl);
    fromMeElRef.current = fromMeEl;
    olMap.addOverlay(new Overlay({ element: fromMeEl, positioning: 'top-left', stopEvent: false }));

    // Click handler
    olMap.on('click', (e) => {
      const feature = olMap.forEachFeatureAtPixel(e.pixel, f => f);
      if (!feature || !contentRef.current) {
        popup.setPosition(undefined);
        return;
      }
      const coord = feature.getGeometry().getCoordinates();
      popup.setPosition(coord);
      const p = feature.getProperties();
      if (p.type === 'stop') {
        contentRef.current.innerHTML = `<strong>${p.name}</strong>`;
      } else if (p.type === 'user') {
        contentRef.current.innerHTML = `<strong>You are here</strong>`;
      } else {
        const speed = thingModel?.latest?.GPS?.speed ?? 'N/A';
        const ts = thingModel?.latest?.GPS?.timestamp
          ? new Date(thingModel.latest.GPS.timestamp).toLocaleTimeString()
          : 'N/A';
        contentRef.current.innerHTML = `
          <strong>Live Location</strong><br/>
          Speed: ${speed} km/h<br/>
          Time: ${ts}
        `;
      }
    });

    setMap(olMap);

    return () => {
      olMap.setTarget(undefined);
      distanceEl.remove();
      fromMeEl.remove();
    };
  }, []);

  /* ---------- 5. DATA UPDATE ---------- */
  useEffect(() => {
    if (!map || !position || !thingModel) return;

    const lat = position.latitude ?? position.lat;
    const lng = position.longitude ?? position.lng;
    if (lat == null || lng == null) return;

    const coord = fromLonLat([lng, lat]);

    // Bus
    liveSrc.clear();
    const busF = new Feature({ geometry: new Point(coord) });
    busF.setStyle(new Style({ image: new Icon({ anchor: [0.5, 1], src: bus, scale: 0.4 }) }));
    liveSrc.addFeature(busF);

    // Stops
    addStops(thingModel.stops, lat, lng);

    // User location
 userSrc.clear();
if (userLocation && userLocation.latitude && userLocation.longitude) {
  const userCoord = fromLonLat([userLocation.longitude, userLocation.latitude]);
  const userF = new Feature({ geometry: new Point(userCoord), type: 'user' });
  userF.setStyle(new Style({
    image: new Circle({
      radius: 9,
      fill: new Fill({ color: '#1976d2' }),
      stroke: new Stroke({ color: '#fff', width: 3 })
    })
  }));
  userSrc.addFeature(userF);

  // LIVE DISTANCE
  const dist = haversine(
    { latitude: userLocation.latitude, longitude: userLocation.longitude },
    { latitude: lat, longitude: lng },
    { unit: 'meter' }
  );
  const fmt = (m) => m > 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;

  if (fromMeElRef.current) {
    fromMeElRef.current.innerHTML = `
      <div style="font-size:15px; font-weight:600; color:white;">
        From Me: <strong style="color:#fff;">${fmt(dist)}</strong>
      </div>
    `;
  }
} else if (fromMeElRef.current) {
  fromMeElRef.current.innerHTML = '';
}

    // Path
    const poly = thingModel.currentJourney?.wayPointsPolyLine;
    let pts = [];
    if (typeof poly === 'string' && poly.trim()) {
      pts = decodePolyline(poly);
    }

    if (pts.length > 0) {
      let bestIdx = 0;
      let bestDist = Infinity;
      pts.forEach((p, i) => {
        const d = haversine(
          { latitude: p[1], longitude: p[0] },
          { latitude: lat, longitude: lng },
          { unit: 'meter' }
        );
        if (d < bestDist) { bestDist = d; bestIdx = i; }
      });

      const covered = pts.slice(0, bestIdx + 1);
      const remain = pts.slice(bestIdx);
      drawLine(covered, '#2bbf9f', coveredSrc);
      drawLine(remain, '#f04c31', remainSrc);

      let total = 0, remaining = 0;
      for (let i = 1; i < pts.length; i++) {
        total += haversine(pts[i - 1], pts[i], { unit: 'meter' });
      }
      for (let i = bestIdx; i < pts.length - 1; i++) {
        remaining += haversine(pts[i], pts[i + 1], { unit: 'meter' });
      }
      if (bestIdx < pts.length - 1) {
        remaining += haversine(
          { latitude: lat, longitude: lng },
          pts[bestIdx + 1],
          { unit: 'meter' }
        );
      }

      const fmt = (m) => m > 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
      if (distanceElRef.current) {
        distanceElRef.current.innerHTML = `
          Remaining: <strong>${fmt(remaining)}</strong><br/>
          <small style="opacity:0.8;">Total: ${fmt(total)}</small>
        `;
      }
    } else {
      coveredSrc.clear();
      remainSrc.clear();
      if (distanceElRef.current) distanceElRef.current.innerHTML = '';
    }

    if (follow) {
      view.animate({ center: coord, zoom: 16, duration: 800 });
    }
  }, [position, thingModel, userLocation, follow, map, addStops]); // FIXED: RandStops → addStops

  /* ---------- 6. Toggle Follow ---------- */
  const toggleFollow = () => {
    const next = !follow;
    setFollow(next);
    if (next && position) {
      const lat = position.latitude ?? position.lat;
      const lng = position.longitude ?? position.lng;
      if (lat != null && lng != null) {
        view.animate({ center: fromLonLat([lng, lat]), zoom: 16, duration: 800 });
      }
    }
  };

  // Auto-center on first position
  useEffect(() => {
    if (map && position && !view.getCenter()) {
      const lat = position.latitude ?? position.lat;
      const lng = position.longitude ?? position.lng;
      if (lat != null && lng != null) {
        view.animate({ center: fromLonLat([lng, lat]), zoom: 15 });
      }
    }
  }, [map, position]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '70vh' }}>
      <Box
        ref={mapEl}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: 1
        }}
      />
      <Tooltip title={follow ? 'Following' : 'Follow vehicle'} placement="left">
        <ToggleButton
          value="follow"
          selected={follow}
          onChange={toggleFollow}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            bgcolor: 'background.paper',
            '&.Mui-selected': { bgcolor: 'primary.main', color: 'white' },
            '&:hover': { bgcolor: follow ? 'primary.dark' : 'grey.300' },
            borderRadius: 2,
            minWidth: 48,
            height: 48
          }}
        >
          <LocationSearchingIcon />
        </ToggleButton>
      </Tooltip>
    </Box>
  );
}

export default Openlayer;